import apiClient from './apiClient';

export interface Appointment {
  id: string;
  clinicId: string;
  locationId?: string;
  roomId?: string;
  doctorId: string;
  clientId: string;
  patientId: string;
  serviceId?: string;
  start: string;
  end: string;
  notes?: string;
  status: 'pending_confirmation' | 'scheduled' | 'confirmed' | 'arrived' | 'in-progress' | 'in_progress' | 'completed' | 'cancelled' | 'no-show' | 'no_show';
  createdAt: string;
  updatedAt: string;
  // Additional fields for compatibility with AppointmentsEnhanced
  date?: string;
  time?: string;
  patientName?: string;
  clientName?: string;
  clientPhone?: string;
  clientEmail?: string;
  veterinarianName?: string;
  veterinarianId?: string;
  serviceName?: string;
  servicePrice?: number;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  // Additional properties for UI compatibility
  animalName?: string;
  animal?: string;
  type?: string;
  urgency?: 'low' | 'medium' | 'high';
  emergency?: boolean;
  clinic?: string;
  veterinarian?: string;
  reason?: string;
  service?: string;
  cost?: number;
  scheduledAt?: string;
  appointmentDate?: string;
  duration?: number;
  locationName?: string;
  roomName?: string;
  animalSpecies?: string;
  veterinarianFirstName?: string;
  veterinarianLastName?: string;
  clinicName?: string;
}

export interface CreateAppointmentData {
  clinicId: string;
  locationId?: string;
  roomId?: string;
  doctorId: string;
  clientId: string;
  patientId: string;
  serviceId?: string;
  start: string;
  end: string;
  notes?: string;
}

export interface UpdateAppointmentData {
  status?: string;
  start?: string;
  end?: string;
  notes?: string;
  doctorId?: string;
  locationId?: string;
  roomId?: string;
  serviceId?: string;
}

export interface AppointmentFilters {
  clinicId?: string;
  patientId?: string;
  doctorId?: string | string[];
  from?: string;
  to?: string;
  status?: string;
  locationId?: string;
  roomId?: string;
  search?: string;
  priority?: string;
  dateFrom?: string;
  dateTo?: string;
  veterinarianId?: string;
  serviceId?: string;
  clientId?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: string;
  groupBy?: 'doctor' | 'room' | 'hour';
  showAllergiesOnly?: boolean;
  showDebtorsOnly?: boolean;
  needsEquipment?: boolean;
}

export interface AppointmentStats {
  total: number;
  confirmed: number;
  pending: number;
  completed: number;
  cancelled: number;
  noShow: number;
  scheduled?: number;
  present?: number;
  averageDuration?: number;
  confirmationRate?: number;
  noShowRate?: number;
  byDay?: Array<{ day: string; count: number; }>;
  byMonth?: Array<{ month: string; count: number; }>;
  byStatus?: Array<{
    status: string;
    count: number;
    percentage: number;
  }>;
  byVeterinarian?: Array<{
    veterinarianId: string;
    veterinarianName: string;
    count: number;
    percentage: number;
    revenue?: number;
  }>;
  byService?: Array<{
    serviceId: string;
    serviceName: string;
    count: number;
    percentage: number;
    revenue?: number;
  }>;
}

export interface Service {
  id: string;
  name: string;
  duration: number; // in minutes
  price: number;
  description?: string;
}

export interface Doctor {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  specialties?: string[];
}

export interface Location {
  id: string;
  name: string;
  address: string;
  rooms: Room[];
}

export interface Room {
  id: string;
  name: string;
  locationId: string;
  capacity: number;
}

class AppointmentsService {
  private getCurrentUserRole(): string | undefined {
    try {
      const raw = localStorage.getItem('user');
      if (!raw) return undefined;
      const user = JSON.parse(raw);
      return user?.role;
    } catch {
      return undefined;
    }
  }

  private toIsoFromDateAndTime(date: string, time: string): string {
    return new Date(`${date}T${time}`).toISOString();
  }

  async getAppointments(filters: AppointmentFilters = {}): Promise<{
    appointments: Appointment[];
    total: number;
    stats?: AppointmentStats;
  }> {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });

      const url = `/appointments?${params}`;
      const response = await apiClient.get(url);

      return {
        appointments: response.data.data || [],
        total: response.data.pagination?.total || 0,
        stats: response.data.stats
      };
    } catch (error) {
      throw error;
    }
  }

  async createAppointment(
    appointmentData: CreateAppointmentData & {
      reason?: string;
      emergency?: boolean;
      durationMinutes?: number;
    }
  ): Promise<Appointment> {
    try {
      const duration =
        appointmentData.durationMinutes ??
        (appointmentData.start && appointmentData.end
          ? Math.max(
              15,
              Math.round(
                (new Date(appointmentData.end).getTime() - new Date(appointmentData.start).getTime()) / 60000
              )
            )
          : 30);
      const payload = {
        clinicId: appointmentData.clinicId,
        patientId: appointmentData.patientId,
        clientId: appointmentData.clientId,
        doctorId: appointmentData.doctorId,
        veterinarianId: appointmentData.doctorId,
        scheduledAt: appointmentData.start,
        start: appointmentData.start,
        end: appointmentData.end,
        duration,
        notes: appointmentData.notes,
        reason:
          appointmentData.reason ??
          (appointmentData.notes ? String(appointmentData.notes).slice(0, 500) : 'Programare standard'),
        emergency: appointmentData.emergency ?? false,
        serviceId: appointmentData.serviceId,
        locationId: appointmentData.locationId,
        roomId: appointmentData.roomId,
      };
      const response = await apiClient.post('/appointments', payload);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to create appointment');
      }
    } catch (error) {
      throw error;
    }
  }

  async convertToVisit(appointmentId: string): Promise<{ visitId: string }> {
    try {
      const response = await apiClient.post(`/appointments/${appointmentId}/convert-to-visit`, {});

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to convert appointment to visit');
      }
    } catch (error) {
      throw error;
    }
  }

  async getServices(clinicId: string): Promise<Service[]> {
    try {
      const response = await apiClient.get(`/services?clinicId=${clinicId}`);

      return response.data.data || [];
    } catch (error) {
      throw error;
    }
  }

  async getDoctors(clinicId: string): Promise<Doctor[]> {
    try {
      const response = await apiClient.get(`/users/doctors?clinicId=${clinicId}`);

      return response.data.data || [];
    } catch (error) {
      throw error;
    }
  }

  async getLocations(clinicId: string): Promise<Location[]> {
    try {
      const response = await apiClient.get(`/services/locations?clinicId=${clinicId}`);

      return response.data.data || [];
    } catch (error) {
      throw error;
    }
  }

  async checkConflicts(data: {
    doctorId: string;
    roomId?: string;
    start: string;
    end: string;
    excludeAppointmentId?: string;
  }): Promise<{ hasConflict: boolean; conflicts: Appointment[] }> {
    try {
      const response = await apiClient.post('/appointments/check-conflicts', data);

      return response.data.data;
    } catch (error) {
      throw error;
    }
  }

  async updateAppointment(appointmentId: string, data: UpdateAppointmentData): Promise<Appointment> {
    try {
      const response = await apiClient.put(`/appointments/${appointmentId}`, data);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to update appointment');
      }
    } catch (error) {
      throw error;
    }
  }

  async updateAppointmentStatus(appointmentId: string, status: string): Promise<Appointment> {
    try {
      const response = await apiClient.patch(`/appointments/${appointmentId}/status`, { status });

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to update appointment status');
      }
    } catch (error) {
      throw error;
    }
  }

  async startAppointment(appointmentId: string): Promise<boolean> {
    try {
      const response = await apiClient.patch(`/appointments/${appointmentId}/start`, {});
      return response.data.success === true;
    } catch (error) {
      throw error;
    }
  }

  async deleteAppointment(appointmentId: string): Promise<void> {
    try {
      const response = await apiClient.delete(`/appointments/${appointmentId}`);

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to delete appointment');
      }
    } catch (error) {
      throw error;
    }
  }

  async confirmAppointment(appointmentId: string, method: string = 'sms'): Promise<boolean> {
    try {
      const response = await apiClient.patch(`/appointments/${appointmentId}/confirm`, {
        method
      });

      return response.data.success;
    } catch (error) {
      throw error;
    }
  }

  async rescheduleAppointment(appointmentId: string, newDate: string, newTime: string, reason: string): Promise<boolean> {
    try {
      const role = this.getCurrentUserRole();
      const scheduledAtIso = this.toIsoFromDateAndTime(newDate, newTime);

      // Clientul solicita reprogramare (request), recepția/admin-ul aplică reprogramarea direct.
      const response = role === 'client'
        ? await apiClient.post(`/appointments/${appointmentId}/request-reschedule`, {
            new_start_ts: scheduledAtIso,
            reason,
          })
        : await apiClient.post(`/reception/appointments/${appointmentId}/reschedule`, {
            scheduledAt: scheduledAtIso,
            reason,
          });

      return response.data.success;
    } catch (error) {
      throw error;
    }
  }

  async cancelAppointment(appointmentId: string, reason: string, applyPenalty: boolean = false): Promise<boolean> {
    try {
      const response = await apiClient.patch(`/appointments/${appointmentId}/cancel`, {
        reason,
        applyPenalty
      });

      return response.data.success;
    } catch (error) {
      throw error;
    }
  }

  async markNoShow(appointmentId: string, applyPenalty: boolean = false): Promise<boolean> {
    try {
      const response = await apiClient.patch(`/appointments/${appointmentId}/no-show`, {
        applyPenalty
      });

      return response.data.success;
    } catch (error) {
      throw error;
    }
  }

  async checkIn(appointmentId: string): Promise<boolean> {
    try {
      const response = await apiClient.post(`/appointments/${appointmentId}/check-in`, {});

      return response.data.success;
    } catch (error) {
      throw error;
    }
  }

  async completeAppointment(appointmentId: string, notes: string, attachments: any[] = []): Promise<boolean> {
    try {
      const response = await apiClient.post(`/appointments/${appointmentId}/complete`, {
        notes,
        attachments
      });

      return response.data.success;
    } catch (error) {
      throw error;
    }
  }

  async exportAppointments(filters: AppointmentFilters, format: string = 'pdf'): Promise<Blob> {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value.toString());
      });
      params.append('format', format);

      const response = await apiClient.get(`/appointments/export?${params}`, {
        responseType: 'blob'
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getAppointmentStats(filters: AppointmentFilters): Promise<AppointmentStats> {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value.toString());
      });

      const response = await apiClient.get(`/appointments/stats?${params}`);

      return response.data.data;
    } catch (error) {
      throw error;
    }
  }

  // ===== METODE PENTRU ADMIN CLINICA =====

  async getConflicts(filters: { clinicId: string; from: string; to: string }): Promise<{ conflicts: any[] }> {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value.toString());
      });

      const response = await apiClient.get(`/appointments/conflicts?${params}`);

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async bulkAction(data: { action: string; ids: string[]; payload?: any }): Promise<boolean> {
    try {
      const response = await apiClient.post('/appointments/bulk', data);

      return response.data.success;
    } catch (error) {
      throw error;
    }
  }

  async autoAllocate(data: { clinicId: string; from: string; to: string }): Promise<boolean> {
    try {
      const response = await apiClient.post('/appointments/auto-allocate', data);

      return response.data.success;
    } catch (error) {
      throw error;
    }
  }

  async getKPIDaily(filters: { clinicId: string; date: string }): Promise<{
    total: number;
    confirmed: number;
    arrived: number;
    inProgress: number;
    noShowRate: number;
    estRevenue: number;
    alerts: number;
  }> {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value.toString());
      });

      const response = await apiClient.get(`/kpi/appointments/daily?${params}`);

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async createScheduleBlock(data: {
    clinicId: string;
    doctorId?: string;
    roomId?: string;
    start: string;
    end: string;
    type: 'break' | 'internal' | 'maintenance';
  }): Promise<boolean> {
    try {
      const response = await apiClient.post('/schedule/blocks', data);

      return response.data.success;
    } catch (error) {
      throw error;
    }
  }

  async getScheduleBlocks(filters: { clinicId: string; from: string; to: string }): Promise<any[]> {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value.toString());
      });

      const response = await apiClient.get(`/schedule/blocks?${params}`);

      return response.data.data || [];
    } catch (error) {
      throw error;
    }
  }

  async deleteScheduleBlock(blockId: string): Promise<boolean> {
    try {
      const response = await apiClient.delete(`/schedule/blocks/${blockId}`);

      return response.data.success;
    } catch (error) {
      throw error;
    }
  }

  // Obtine statisticile no-show pentru un client/proprietar
  async getClientNoShowStats(clientId: string) {
    try {
      const response = await apiClient.get(`/appointments/client/${clientId}/no-show-stats`);

      return response.data;
    } catch (error) {
      throw error;
    }
  }
}

export const appointmentsService = new AppointmentsService();
